import { evaluate } from "mathjs";

import { FORMULA_EVAL_ORDER } from "../../../../../constants/formulaEvalOrder";
import type { LegacyDependencyRule } from "../../../../../types/productDependencies";
import {
    applyRidgeVentScopeMapping,
    applyRoofVentCountScopeMapping,
    buildSelectionsScope,
    resolveFormulaSubcategoryKey,
    sanitizeVariableName,
} from "../../../../../utils/formulaScope";

/**
 * Helper functions for product selection and dependency management
 */

export function hasColorOptions(materialSchema: any[], productCategory: string, productName: string): boolean {
    const categorySchema = materialSchema.find((schemaItem: any) => schemaItem.Name === productCategory);
    const selectedProductSubcategories =
        categorySchema?.Subcategories?.filter((sub: any) => sub.Name === productName) || [];
    return selectedProductSubcategories.some((sub: any) => sub.Color);
}

export function findSubcategory(
    materialSchema: any[],
    category: string,
    productNameOrId: string,
): { Id?: string; Name?: string; SystemLine?: string; Color?: string } | undefined {
    if (!materialSchema || !Array.isArray(materialSchema)) return undefined;

    const categorySchema = materialSchema.find((schemaItem: any) => {
        const name = (schemaItem.Name || schemaItem.ProductName || schemaItem.name || "").trim().toLowerCase();
        return name === category.trim().toLowerCase();
    });
    if (!categorySchema?.Subcategories) return undefined;

    const key = productNameOrId.trim().toLowerCase();
    return categorySchema.Subcategories.find(
        (sub: any) => sub.Id === productNameOrId || (sub.Name || "").trim().toLowerCase() === key,
    );
}

export function findManufacturerProductId(
    materialSchema: any[],
    category: string,
    productName: string,
): string | undefined {
    if (!materialSchema || !Array.isArray(materialSchema)) {
        return undefined;
    }

    const categorySchema = materialSchema.find((schemaItem: any) => {
        const name = (schemaItem.Name || schemaItem.ProductName || schemaItem.name || "").trim().toLowerCase();
        return name === category.trim().toLowerCase();
    });

    if (!categorySchema || !categorySchema.Subcategories) {
        return undefined;
    }

    const normalizedProductName = productName.trim().toLowerCase();
    const matchingSubs = categorySchema.Subcategories.filter(
        (sub: any) => (sub.Name || "").trim().toLowerCase() === normalizedProductName,
    );

    if (matchingSubs.length === 0) {
        return undefined;
    }

    const defaultSub = matchingSubs.find((sub: any) => !sub.Color);
    return defaultSub?.Id || matchingSubs[0]?.Id;
}

/** Child categories auto-filled from mapping requiredProducts when the parent product changes. */
export function getMappedProductCategories(rule: LegacyDependencyRule | undefined): string[] {
    if (!rule) return [];

    const categories = new Set<string>();
    rule.Mappings.forEach((mapping) => {
        mapping.RequiredProducts.forEach((req) => categories.add(req.Category));
    });
    return Array.from(categories);
}

/** Categories whose prerequisiteCategories include `category` (cleared when prerequisite is removed). */
export function getDependentCategories(dependencies: LegacyDependencyRule[], category: string): string[] {
    return dependencies.filter((rule) => rule.PrerequisiteCategories.includes(category)).map((rule) => rule.Category);
}

function resolveFormulaExpression(
    formulas: any,
    category: string,
    productName: string,
    prefilledValues?: any,
): string | undefined {
    const categoryFormula = formulas?.[category];
    if (!categoryFormula) return undefined;

    let expression = categoryFormula.expression;

    if (categoryFormula.subcategories && productName) {
        const subKey = resolveFormulaSubcategoryKey(category, productName, prefilledValues);
        const subFormula = categoryFormula.subcategories[subKey] || categoryFormula.subcategories[productName];
        if (subFormula?.expression) {
            expression = subFormula.expression;
        }
    }

    return expression;
}

function parseMaterialQuantity(raw: unknown): number | undefined {
    if (raw === null || raw === undefined || raw === "") return undefined;
    if (typeof raw === "number") return isNaN(raw) ? undefined : raw;
    const parsed = Number(raw);
    return isNaN(parsed) ? undefined : parsed;
}

/** Inject prior material qty/computed results so formulas like `FieldShingles * 2` resolve. */
function injectPriorMaterialQuantities(
    scope: Record<string, any>,
    category: string,
    prefilledValues: Record<string, any> | undefined,
    formulas: any,
    inspectionScopeKeys: Set<string>,
): void {
    if (!prefilledValues || !formulas) return;

    for (const materialCategory of FORMULA_EVAL_ORDER) {
        if (materialCategory === category) continue;

        const key = sanitizeVariableName(materialCategory);
        if (inspectionScopeKeys.has(key)) continue;
        if (key in scope) continue;

        const sel = prefilledValues[materialCategory];
        if (!sel?.value) continue;

        const cartQty = parseMaterialQuantity(sel.quantity);
        if (cartQty !== undefined) {
            scope[key] = cartQty;
            continue;
        }

        const expr = resolveFormulaExpression(formulas, materialCategory, sel.value, prefilledValues);
        if (!expr) continue;

        try {
            const computed = evaluate(expr, scope) as number;
            if (!isNaN(computed) && computed !== Infinity && computed !== -Infinity) {
                scope[key] = Math.round(computed * 100) / 100;
            }
        } catch {
            // Missing measurement — skip
        }
    }
}

/** Re-evaluate quantities for categories whose formulas reference `changedCategory`. */
export function recalculateDependentQuantities(
    next: Record<string, any>,
    changedCategory: string,
    dependencies: LegacyDependencyRule[],
    materialSchema: any[],
    measurements: any[],
    formulas: any,
    attributes: any[],
): void {
    if (!formulas) return;

    const changedVar = sanitizeVariableName(changedCategory);
    const prerequisiteDependents = new Set(getDependentCategories(dependencies, changedCategory));

    for (const [category, sel] of Object.entries(next)) {
        if (category === changedCategory || !sel?.value || sel.awaitingColor) continue;

        const expression = resolveFormulaExpression(formulas, category, sel.value, next);
        const referencesChangedCategory = expression?.includes(changedVar) ?? false;

        if (!prerequisiteDependents.has(category) && !referencesChangedCategory) continue;

        next[category] = {
            ...sel,
            quantity: calculateQuantity(materialSchema, measurements, category, sel.value, formulas, attributes, next),
        };
    }
}

function findParentMapping(
    rule: LegacyDependencyRule | undefined,
    category: string,
    product: string,
    parentManufacturerProductId?: string,
) {
    if (!rule) return undefined;

    return rule.Mappings.find(
        (map) =>
            map.Product === product ||
            map.parentManufacturerProductId === parentManufacturerProductId ||
            (parentManufacturerProductId && map.parentManufacturerProductId === parentManufacturerProductId),
    );
}

/**
 * Recursively sets dependencies for a selected product using API-provided ID mappings.
 */
export function setDependencies(
    dependencies: LegacyDependencyRule[],
    tempStateData: any,
    category: string,
    product: string,
    materialSchema: any[],
    measurements: any[],
    formulas?: any,
    attributes?: any[],
    parentManufacturerProductId?: string,
): void {
    const rule = dependencies.find((dep) => dep.Category === category);
    const parentId =
        parentManufacturerProductId ||
        tempStateData[category]?.manufacturerProductId ||
        findManufacturerProductId(materialSchema, category, product);

    const mapping = findParentMapping(rule, category, product, parentId);
    const deps = mapping?.RequiredProducts || [];

    for (const dep of deps) {
        const formulaKey = dep.formulaProductName || dep.Product;
        const calculatedQuantity = calculateQuantity(
            materialSchema,
            measurements,
            dep.Category,
            formulaKey,
            formulas,
            attributes,
            tempStateData,
        );

        if (calculatedQuantity <= 0) {
            continue;
        }

        const manufacturerProductId =
            dep.manufacturerProductId || findManufacturerProductId(materialSchema, dep.Category, dep.Product);

        const depSub = manufacturerProductId
            ? findSubcategory(materialSchema, dep.Category, manufacturerProductId)
            : findSubcategory(materialSchema, dep.Category, dep.Product);

        tempStateData[dep.Category] = {
            value: dep.Product,
            quantity: calculatedQuantity,
            ...(manufacturerProductId ? { manufacturerProductId } : {}),
            ...(depSub?.SystemLine ? { systemLine: depSub.SystemLine } : {}),
        };

        setDependencies(
            dependencies,
            tempStateData,
            dep.Category,
            dep.Product,
            materialSchema,
            measurements,
            formulas,
            attributes,
            dep.manufacturerProductId,
        );
    }
}

export function calculateQuantity(
    materialSchema: any[],
    measurements: any[],
    category: string,
    productName: string,
    formulas?: any,
    attributes?: any[],
    prefilledValues?: any,
): number {
    if (!formulas) {
        return 0;
    }

    const expression = resolveFormulaExpression(formulas, category, productName, prefilledValues);
    const categoryFormula = formulas[category];
    let outputFormat = categoryFormula?.outputFormat;

    if (categoryFormula?.subcategories && productName) {
        const subKey = resolveFormulaSubcategoryKey(category, productName, prefilledValues);
        const subFormula = categoryFormula.subcategories[subKey] || categoryFormula.subcategories[productName];
        if (subFormula) {
            outputFormat = subFormula.outputFormat;
        }
    }

    if (!expression) {
        if (category === "Head Wall Metal") {
            console.warn(
                `[calculateQuantity] No formula expression for Category: "${category}", Product: "${productName}"`,
            );
        }
        return 0;
    }

    try {
        const scope: Record<string, any> = {};
        /** Keys sourced from roof/inspection measurements — must not be overwritten by cart product qty */
        const inspectionScopeKeys = new Set<string>();

        if (Array.isArray(measurements)) {
            measurements.forEach((m) => {
                const value = m.ActualValue ?? m.CalculatedValue;
                const numVal = Number(value);
                const val = !isNaN(numVal) && value !== "" && value !== null && value !== undefined ? numVal : value;
                const key = sanitizeVariableName(m.MeasurementType);
                scope[key] = val;
                inspectionScopeKeys.add(key);
            });

            // Legacy measurement type names → formula variable names
            if (scope.Headwall != null && scope.Headwall !== "") {
                scope.HeadWallMetal = Number(scope.Headwall) || scope.HeadWallMetal;
                inspectionScopeKeys.add("HeadWallMetal");
            }
            if (scope.Step != null && scope.Step !== "") {
                scope.StepFlashing = Number(scope.Step) || scope.StepFlashing;
                inspectionScopeKeys.add("StepFlashing");
            }
            if (scope.RollMetal != null && scope.RollMetal !== "") {
                scope.RollValley = Number(scope.RollMetal) || scope.RollValley;
                inspectionScopeKeys.add("RollValley");
            }
            if (scope.WValleyOrRollValley != null && scope.WValleyOrRollValley !== "") {
                scope.RollValley = Number(scope.WValleyOrRollValley) || scope.RollValley;
                inspectionScopeKeys.add("RollValley");
            }
        }

        const defaults: Record<string, any> = {
            Area: 0,
            Eave: 0,
            Rake: 0,
            Ridge: 0,
            Hip: 0,
            Valley: 0,
            HeadWallMetal: 0,
            StepFlashing: 0,
            RollValley: 0,
            WValleyOrRollValley: 0,
            CounterFlashing: 0,
            RoofVents: 0,
            BoxVents: 0,
            TurbineVents: 0,
            RVs: 0,
            Skylight: 0,
            Skylights: 0,
            Chimney: 0,
            PlumbingJacks: 0,
            Pjack: 0,
            SplitBoots: 0,
            SplitsBoots: 0,
            FurnaceVents: 0,
            Furn: 0,
            BathroomVents: 0,
            BathVents: 0,
            DryerVents: 0,
            GutterLength: 0,
            ScreenLength: 0,
            DownspoutLength: 0,
            SolarPanels: 0,
            Cricket: 0,
            Cooler: 0,
            InstallNewGutters: "false",
            InstallNewDownspouts: "false",
            RemoveExistingGutters: "false",
            IsOpenWValley: 0,
        };
        Object.entries(defaults).forEach(([key, val]) => {
            if (!(key in scope)) scope[key] = val;
        });

        if (Array.isArray(attributes)) {
            attributes.forEach((attr) => {
                const rawName: string = attr.AttributeName;
                if (!rawName) return;

                const key = sanitizeVariableName(rawName);

                // Visual inspection items store LF/count in Qty; AttributeValue may be stale "0" from project init
                if (attr.Qty !== null && attr.Qty !== undefined && attr.Qty !== "") {
                    const qtyNum = Number(attr.Qty);
                    if (!isNaN(qtyNum)) {
                        scope[key] = qtyNum;
                        inspectionScopeKeys.add(key);
                        return;
                    }
                }

                let raw = attr.AttributeValue;
                if (raw === null || raw === undefined || raw === "") {
                    if (
                        attr.CalculatedValue !== null &&
                        attr.CalculatedValue !== undefined &&
                        attr.CalculatedValue !== ""
                    ) {
                        const calcNum = Number(attr.CalculatedValue);
                        if (!isNaN(calcNum)) {
                            scope[key] = calcNum;
                            inspectionScopeKeys.add(key);
                        }
                    }
                    return;
                }

                let parsed: any;
                try {
                    parsed = JSON.parse(raw);
                } catch {
                    parsed = null;
                }

                let value: any;
                if (Array.isArray(parsed)) {
                    const total = parsed.reduce((sum: number, item: any) => sum + (Number(item.count) || 0), 0);
                    value = total > 0 ? total : parsed.length;
                } else {
                    const num = Number(raw);
                    value = !isNaN(num) && raw !== "" ? num : raw;
                }

                scope[key] = value;
                inspectionScopeKeys.add(key);
            });
        }

        const valleyType = attributes?.find((attr) => attr.AttributeName === "Valley Type")?.AttributeValue;
        const isOpenWValley = valleyType === "Open (W) Valley" || valleyType === "Open Metal";
        const isClosedValley = valleyType === "Closed Valley";

        if (isOpenWValley) {
            scope.IsOpenWValley = 1;
        }

        if (isClosedValley && (!scope.RollValley || Number(scope.RollValley) === 0) && Number(scope.Valley) > 0) {
            scope.RollValley = Number(scope.Valley);
            inspectionScopeKeys.add("RollValley");
        }

        applyRidgeVentScopeMapping(scope, attributes, inspectionScopeKeys);
        applyRoofVentCountScopeMapping(scope, attributes, inspectionScopeKeys);

        Object.assign(scope, buildSelectionsScope(prefilledValues));

        if (prefilledValues) {
            Object.entries(prefilledValues).forEach(([prod, data]: [string, any]) => {
                const key = sanitizeVariableName(prod);
                if (prod === category || inspectionScopeKeys.has(key)) return;

                const qty = parseMaterialQuantity(data.quantity);
                if (qty !== undefined) {
                    scope[key] = qty;
                }
            });
        }

        injectPriorMaterialQuantities(scope, category, prefilledValues, formulas, inspectionScopeKeys);

        let result = evaluate(expression, scope) as number;
        if (category === "Ice & Water Shield" || category === "Head Wall Metal") {
            console.log(
                `[calculateQuantity] Evaluating formula for Category: "${category}", Product: "${productName}"`,
            );
            console.log(`  Expression: "${expression}"`);
            console.log(
                `  HeadWallMetal=${scope.HeadWallMetal} (inspection-protected=${inspectionScopeKeys.has("HeadWallMetal")})`,
            );
            console.log(`  Scope:`, scope);
            console.log(`  Result: ${result}`);
        }

        result = Math.round(result * 100) / 100;
        return isNaN(result) || result === Infinity || result === -Infinity ? 0 : result;
    } catch (error) {
        console.error(`Error calculating quantity for ${category} / ${productName}:`, error);
        return 0;
    }
}

export function deriveColorFromSchema(
    materialSchema: any[],
    manufacturerProductId: string | null | undefined,
    productCategory: string,
): string | undefined {
    if (!manufacturerProductId || !Array.isArray(materialSchema)) {
        return undefined;
    }

    const categoryEntry = materialSchema.find((prod: any) => {
        const name = prod.Name || prod.ProductName || prod.name;
        return name === productCategory;
    });

    const sub = categoryEntry?.Subcategories?.find((s: any) => s.Id === manufacturerProductId && s.Color);
    return sub?.Color as string | undefined;
}
